
<template>
    <!-- Chart Content Wrapper -->
    <div class="chart-content-wrapper">
        <div class="chart-wrapper" :key="selectedCurrency + selectedProduct + selectedTab +  + getAppThemeMode">
            <chart-complex
            identifier="patrimony"
            :graphData="graphData"
            :colors="hideLabels ? [notSelectedColor] : colorsForGraph"
            :centerPercentageParams="centerPercentageParams"
            :hoverIndex="chartIsRendered ? centerPercentageParams.index : -1"
            :showZeroTooltip="centerPercentageParams.percentage === 0"
            isMobile
            hideLabels
            @chartIsRendered="chartIsRendered = $event" />
        </div>
        <!-- Product cards -->
        <product-cards-container
        v-if="allDisplayData.length"
        :key="index"
        :selectedProduct="selectedProduct"
        :selectedCurrency="selectedCurrency"
        :products="allDisplayData"
        :colors="colors"
        :selectedTab="selectedTab"
        @cardClicked="handleLabelClick"
        @scrolledToCard="setCenterPercentageParams" />
    </div>
</template>

<script setup>
import ChartComplex from '@/components/generic/charts/Complex'
import ProductCardsContainer from '@/components/features/accounts/patrimony/chart/mobile/ProductCardsContainer'
/* Vue & Assets */
import { ref, computed, watch, onMounted } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { getAccountRouteName } from '@/assets/scripts/mappers/accounts'
import {
    calculateActivePassivePercentage, createActivePassiveGraphArray, calculateProductTotalAmount,
    calculateProductPercentage, createProductGraphArray, calculateArrayAccountsPercentage, createAccountGraphArray,
    convertHexToHSL, createColorBasedArray
} from '@/assets/scripts/parsers/patrimony'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['resetPickerDrops', 'productChanged', 'updateSelectedTab', 'showEmptyState'])

/** props received
@selectedCurrency {string} - selected currency in our picker
@selectedProduct {string} - selected product in our picker
@savedBackState {Object} - saved back state
@selectedTab {String, Number}
*/
const props = defineProps({
    selectedCurrency: String,
    selectedProduct: String,
    savedBackState: Object,
    selectedTab: [String, Number]
})

const { savePatrimonyBackState } = mapActions(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAccountsProductsList, getPatrimonyAccountsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAppThemeMode } = mapGetters()

/**********************************************************************
*                                                                     *
*                             Charts Logic                            *
*                                                                     *
**********************************************************************/
const clusterLength = ref(0)
const notSelectedColor = ref('#ebf1f3')
const assetsVsLiabilitiesColors = ref(['var(--cashflow-entry)', 'var(--cashflow-exit)'])
const centerPercentageParams = ref({ index: -1 })
const chartIsRendered = ref(false)

const allDisplayData = computed(() => {
    const selectedCurrency = props.selectedCurrency === 'allCurrencies' ? null : props.selectedCurrency
    const selectedProduct = props.selectedProduct === 'allProducts' ? null : props.selectedProduct

    if (props.selectedTab === 'assetsVsLiabilities') {
        return getAccountFamilyGraph('labels', selectedCurrency)
    } else {
        if (selectedProduct) {
            return getAccountsGraph('labels', selectedCurrency, selectedProduct)
        } else {
            return getProductGraph('labels', selectedCurrency, props.selectedTab)
        }
    }
})

const graphData = computed(() => {
    const selectedCurrency = props.selectedCurrency === 'allCurrencies' ? null : props.selectedCurrency
    const selectedProduct = props.selectedProduct === 'allProducts' ? null : props.selectedProduct

    if (props.selectedTab === 'assetsVsLiabilities') {
        return getAccountFamilyGraph('chart', selectedCurrency)
    } else {
        if (selectedProduct) {
            return getAccountsGraph('chart', selectedCurrency, selectedProduct)
        } else {
            return getProductGraph('chart', selectedCurrency, props.selectedTab)
        }
    }
})

const hideLabels = computed(() => {
    return graphData.value.length && graphData.value[0][0] === 'empty'
})

/*
* We need to define our colors array based on:
*	- accountFinancialType being 'assetsVsLiabilities', it only has positive or negative color
*	- allProducts option, we create an array with only the colors of products available for this filter
*	- specific product, we create an array with the product color as base
*/
const colors = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') return assetsVsLiabilitiesColors.value

    return props.selectedProduct === 'allProducts' ? createProductsPalette() : createAccountsPalette()
})

/*
* We need to have a diferent array for graph, since we only show positive values
*/
const colorsForGraph = computed(() => {
    if (props.selectedTab === 'assetsVsLiabilities') return assetsVsLiabilitiesColors.value

    return props.selectedProduct === 'allProducts' ? createProductsPalette(true) : createAccountsPalette(true)
})

/*
* Graphic to compare assets and liabilities
*/
const getAccountFamilyGraph = (displayType, selectedCurrency) => {
    let totalAmount = 0
    let assetsTotal = 0
    let liabilitiesTotal = 0

    getPatrimonyAccountsList.value(selectedCurrency, null, 'assets').forEach(account => {
        assetsTotal += account.convertedBalance
        totalAmount += account.convertedBalance
    })

    getPatrimonyAccountsList.value(selectedCurrency, null, 'liabilities').forEach(account => {
        liabilitiesTotal += Math.abs(account.convertedBalance)
        totalAmount += Math.abs(account.convertedBalance)
    })

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && assetsTotal === 0 && liabilitiesTotal === 0) return [['empty', 100]]

    const graphArray = [
        {
            description: t('accounts.patrimony.labels.assets'),
            totalAmount: assetsTotal,
            accounts: getPatrimonyAccountsList.value(null, null, 'assets')
        },
        {
            description: t('accounts.patrimony.labels.liabilities'),
            totalAmount: liabilitiesTotal,
            accounts: getPatrimonyAccountsList.value(null, null, 'liabilities')
        }
    ]

    return displayType === 'chart'
        ? createActivePassiveGraphArray(calculateActivePassivePercentage(graphArray, totalAmount))
        : calculateActivePassivePercentage(graphArray, totalAmount)
}

/*
* Graphic to show a single Product based in selectedCurrency and accountFinancialType
* This chart will show accounts with that product
*/
const getAccountsGraph = (displayType, selectedCurrency, selectedProduct) => {
    const graphArray = []
    const accountsList = getPatrimonyAccountsList.value(selectedCurrency, selectedProduct)
    let totalAmount = 0

    if (accountsList.length) {
        accountsList.forEach(account => {
            totalAmount += account.convertedBalance
            graphArray.push(account)
        })
    }

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && displayType === 'chart') return [['empty', 100]]

    let graphData
    if (displayType === 'chart') {
        graphData = createAccountGraphArray(calculateArrayAccountsPercentage(graphArray, totalAmount)).graph
        clusterLength.value = createAccountGraphArray(calculateArrayAccountsPercentage(graphArray, totalAmount)).cluster.length
    }
    return displayType === 'chart' ? graphData : calculateArrayAccountsPercentage(graphArray, totalAmount)
}

/*
* Graphic with all Products based in selectedCurrency and accountFinancialType
*/
const getProductGraph = (displayType, selectedCurrency, accountFinancialType) => {
    const graphArray = []
    let totalAmount = 0

    getAccountsProductsList.value(null, getAppThemeMode.value).forEach(product => {
        if (getPatrimonyAccountsList.value(selectedCurrency, product.id, accountFinancialType).length) {
            const productInfo = {
                id: product.id,
                color: product.color,
                description: product.description,
                accounts: getPatrimonyAccountsList.value(selectedCurrency, product.id, accountFinancialType)
            }

            productInfo.totalAmount = calculateProductTotalAmount(productInfo.accounts)
            totalAmount += productInfo.totalAmount
            graphArray.push(productInfo)
        }
    })

    // We have to create this empty array, to be display "empty" chart when we have values, but their total % equals 0
    if (totalAmount === 0 && displayType === 'chart') return [['empty', 100]]

    return displayType === 'chart' ? createProductGraphArray(calculateProductPercentage(graphArray, totalAmount)) : calculateProductPercentage(graphArray, totalAmount)
}

/**
* Creates an array with only the colors of products available for this filter
* We use allDisplayData, cuz it has all the info including products with 0%
@onlyPositiveValues {Boolean} - used to filter colors of products with 0%, used for chart since it only receives positive products
*/
const createProductsPalette = onlyPositiveValues => {
    if (onlyPositiveValues) return allDisplayData.value.filter(obj => obj.percentage !== 0).map(obj => { return obj.color })

    return allDisplayData.value.map(obj => { return obj.color })
}

/**
* Creates an array with with selected product as the base
* We use allDisplayData, cuz it has all the info including accounts with 0%
@onlyPositiveValues {Boolean} - used to filter colors of accounts with 0%, used for chart since it only receives positive accounts
*/
const createAccountsPalette = onlyPositiveValues => {
    const baseColor = getAccountsProductsList.value(null, getAppThemeMode.value).find(obj => obj.id === props.selectedProduct).color
    const hslBaseColor = convertHexToHSL(baseColor)
    const auxColor = createColorBasedArray(hslBaseColor, allDisplayData.value.length)
    const auxData = allDisplayData.value.map((obj, index) => {
        return { ...obj, color: auxColor[index] }
    })

    if (onlyPositiveValues) return auxData.filter(obj => obj.percentage !== 0).map(obj => { return obj.color })

    return auxColor
}

/**********************************************************************
*                                                                     *
*                     Handle for Chart Hovers&Clicks                  *
*                                                                     *
**********************************************************************/
const handleLabelClick = item => {
    // if we are viewing charts for assetsVsLiabilities there's no action for this click
    if (props.selectedTab === 'assetsVsLiabilities') return

    props.selectedProduct === 'allProducts' ? emit('productChanged', item.id) : handleAccountClick(item)
}

const handleAccountClick = account => {
	navigateByName(getAccountRouteName(account.accountType), { account: account.id, navigateBack: true })
    savePatrimonyBackState(props.savedBackState)
}

const setCenterPercentageParams = event => {
    let tempIndex = -1

    if (event.percentage > 0) {
        tempIndex = graphData.value.findIndex(data => data[2] === event.id)
    }

    centerPercentageParams.value = {
        ...event,
        index: tempIndex
    }
}
/**********************************************************************
*                                                                     *
*                                 WATCHS                              *
*                                                                     *
**********************************************************************/
watch(() => props.selectedTab, (newVal, oldVal) => {
    if (newVal === 'assetsVsLiabilities') emit('resetPickerDrops')
})

// watch(() => props.selectedCurrency, (newVal, oldVal) => {
//     if (props.selectedTab === 'assetsVsLiabilities' && newVal !== 'allCurrencies') emit('updateSelectedTab', 'assets')
// })

watch(() => props.selectedProduct, () => {
    emit('updateSelectedTab', 'assets')
})

watch(() => props.selectedCurrency, () => {
    if (allDisplayData.value.length === 0) emit('showEmptyState')
})

onMounted(() => {
    if (allDisplayData.value.length === 0) emit('showEmptyState')

    if (props.savedBackState && props.savedBackState.tabSelected) emit('updateSelectedTab', props.savedBackState.tabSelected)
})
</script>

<style lang="scss" scoped>
.chart-content-wrapper {
    width: 100%;

    .chart-wrapper {
        width: 100%;

        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
